import React, { useState } from 'react';
import {
   Box,
   Typography,
   Button,
   Container,
   Grid,
   Card,
   CardContent,
   TextField,
   Select,
   MenuItem,
   FormControl,
   InputLabel,
   Checkbox,
   FormControlLabel,
   FormGroup,
   FormHelperText,
   AppBar,
   Toolbar,
   IconButton,
   useTheme,
   useMediaQuery,
   keyframes,
   alpha,
   Paper,
} from '@mui/material';
import {
   Groups as GroupsIcon,
   Star as StarIcon,
   FlashOn as ZapIcon,
   Favorite as HeartIcon,
   Public as GlobeIcon,
   Adjust as TargetIcon,
   EmojiEvents as AwardIcon,
   CheckCircle as CheckCircleIcon,
   ArrowForward as ArrowForwardIcon,
   Rocket as RocketIcon,
   Security as ShieldIcon,
   Chat as MessageCircleIcon,
   Event as CalendarIcon,
   AutoAwesome as SparklesIcon,
   TrendingUp as TrendingUpIcon,
   Home as HomeIcon,
   Menu as MenuIcon,
   Close as CloseIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/images/logo.png';

// Animations matching landing page
const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-8px) rotate(1deg); }
  66% { transform: translateY(-4px) rotate(-1deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

interface AmbassadorFormData {
   firstName: string;
   lastName: string;
   email: string;
   phone: string;
   cityState: string;
   communityType: string[];
   experienceLength: string;
   communityStory: string;
   meaningfulConnection: string;
   passion: string;
   currentTools: string;
   idealPlatform: string;
   socialPlatforms: string;
   additionalInfo: string;
}

const Ambassador: React.FC = () => {
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
   const navigate = useNavigate();
   const [isSubmitted, setIsSubmitted] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
   const [errors, setErrors] = useState<Partial<AmbassadorFormData>>({});
   const [formData, setFormData] = useState<AmbassadorFormData>({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      cityState: '',
      communityType: [],
      experienceLength: '',
      communityStory: '',
      meaningfulConnection: '',
      passion: '',
      currentTools: '',
      idealPlatform: '',
      socialPlatforms: '',
      additionalInfo: '',
   });

   const handleInputChange = (field: keyof AmbassadorFormData, value: any) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear error when user starts typing
      if (errors[field]) {
         setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
   };

   const validateForm = (): boolean => {
      const newErrors: Partial<AmbassadorFormData> = {};

      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) {
         newErrors.email = 'Email is required';
      } else if (!/^\S+@\S+$/i.test(formData.email)) {
         newErrors.email = 'Please enter a valid email address';
      }
      if (!formData.cityState.trim()) newErrors.cityState = 'City and state are required';
      if (formData.communityType.length === 0)
         newErrors.communityType = 'Please select at least one community type' as any;
      if (!formData.experienceLength)
         newErrors.experienceLength = 'Please select your experience length';
      if (!formData.communityStory.trim())
         newErrors.communityStory = "Please tell us about a community you've helped build";
      if (!formData.meaningfulConnection.trim())
         newErrors.meaningfulConnection = 'Please share a meaningful connection example';
      if (!formData.passion.trim())
         newErrors.passion = 'Please tell us about your passion for community building';
      if (!formData.currentTools.trim())
         newErrors.currentTools = 'Please tell us about your current tools and process';

      // Word count validations
      if (formData.communityStory.length > 1000)
         newErrors.communityStory = 'Please keep to 200 words or less';
      if (formData.meaningfulConnection.length > 750)
         newErrors.meaningfulConnection = 'Please keep to 150 words or less';
      if (formData.passion.length > 750) newErrors.passion = 'Please keep to 150 words or less';
      if (formData.currentTools.length > 750)
         newErrors.currentTools = 'Please keep to 150 words or less';
      if (formData.idealPlatform.length > 500)
         newErrors.idealPlatform = 'Please keep to 100 words or less';
      if (formData.additionalInfo.length > 500)
         newErrors.additionalInfo = 'Please keep to 100 words or less';

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
   };

   const onSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!validateForm()) {
         return;
      }

      try {
         setIsSubmitting(true);
         console.log('Submitting ambassador application:', formData);

         const response = await fetch('/api/ambassador/submit', {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData),
         });

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to submit application');
         }

         const result = await response.json();
         console.log('Ambassador application submitted successfully:', result);

         setIsSubmitted(true);
         window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (error) {
         console.error('Submission error:', error);
         // You could add error state handling here if needed
         alert('Failed to submit application. Please try again.');
      } finally {
         setIsSubmitting(false);
      }
   };

   const scrollToForm = () => {
      const formElement = document.getElementById('application-form');
      if (formElement) {
         formElement.scrollIntoView({ behavior: 'smooth' });
      }
   };

   if (isSubmitted) {
      return (
         <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            {/* Navigation */}
            <AppBar
               position='fixed'
               sx={{
                  background:
                     'linear-gradient(135deg, rgba(1, 148, 154, 0.95) 0%, rgba(0, 67, 105, 0.95) 100%)',
                  backdropFilter: 'blur(15px)',
                  boxShadow: '0 2px 15px rgba(1, 148, 154, 0.2)',
                  zIndex: 1100,
                  color: 'white',
                  border: 'none',
                  borderRadius: 0,
               }}
            >
               <Toolbar sx={{ justifyContent: 'space-between', py: 0.5 }}>
                  <Box
                     sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                     onClick={() => navigate('/')}
                  >
                     <img
                        src={logo}
                        alt='WeTrippen Logo'
                        style={{ width: 32, height: 32, borderRadius: 6 }}
                     />
                     <Typography
                        variant='h6'
                        sx={{ fontWeight: 600, color: 'white', fontSize: '1.1rem' }}
                     >
                        WeTrippen
                     </Typography>
                  </Box>

                  {!isMobile ? (
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Button
                           component={RouterLink}
                           to='/login'
                           sx={{
                              color: 'rgba(255,255,255,0.9)',
                              textTransform: 'none',
                              fontSize: '0.9rem',
                              fontWeight: 400,
                           }}
                        >
                           Sign In
                        </Button>
                        <Button
                           component={RouterLink}
                           to='/signup'
                           variant='contained'
                           sx={{
                              background: 'rgba(255,255,255,0.2)',
                              color: 'white',
                              textTransform: 'none',
                              fontWeight: 500,
                              borderRadius: 2,
                              px: 2.5,
                              py: 0.5,
                              fontSize: '0.9rem',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(255,255,255,0.3)',
                              '&:hover': {
                                 background: 'rgba(255,255,255,0.3)',
                                 transform: 'translateY(-1px)',
                              },
                           }}
                        >
                           Get Started
                        </Button>
                     </Box>
                  ) : (
                     <IconButton
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        sx={{ color: 'white' }}
                     >
                        {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                     </IconButton>
                  )}
               </Toolbar>

               {/* Mobile Menu */}
               {isMobile && mobileMenuOpen && (
                  <Box
                     sx={{ bgcolor: 'white', borderTop: '1px solid', borderColor: 'divider', p: 2 }}
                  >
                     <Button
                        fullWidth
                        component={RouterLink}
                        to='/login'
                        sx={{ justifyContent: 'flex-start', mb: 1, color: 'text.primary' }}
                     >
                        Sign In
                     </Button>
                     <Button
                        fullWidth
                        component={RouterLink}
                        to='/signup'
                        variant='contained'
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                     >
                        Get Started
                     </Button>
                  </Box>
               )}
            </AppBar>

            {/* Success Message */}
            <Box
               sx={{
                  pt: 20,
                  pb: 10,
                  background:
                     'linear-gradient(135deg, rgba(72, 187, 120, 0.1) 0%, rgba(66, 153, 225, 0.1) 100%)',
               }}
            >
               <Container maxWidth='md'>
                  <Box sx={{ textAlign: 'center' }}>
                     <Box
                        sx={{
                           width: 80,
                           height: 80,
                           bgcolor: 'success.main',
                           borderRadius: '50%',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           mx: 'auto',
                           mb: 4,
                        }}
                     >
                        <CheckCircleIcon sx={{ fontSize: 40, color: 'white' }} />
                     </Box>
                     <Typography
                        variant='h2'
                        sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}
                     >
                        Application Submitted Successfully!
                     </Typography>
                     <Typography
                        variant='h6'
                        sx={{ color: 'text.secondary', mb: 4, maxWidth: 600, mx: 'auto' }}
                     >
                        Thank you for your interest in becoming a WeTrippen Community Ambassador.
                        We'll review your application and respond within two weeks.
                     </Typography>
                     <Typography variant='body1' sx={{ color: 'text.secondary', mb: 4 }}>
                        We're excited to learn more about your community building experience and how
                        we can support your efforts to bring people together.
                     </Typography>
                     <Button
                        variant='contained'
                        size='large'
                        onClick={() => navigate('/')}
                        sx={{ px: 4, py: 1.5 }}
                     >
                        Return to Home
                     </Button>
                  </Box>
               </Container>
            </Box>
         </Box>
      );
   }

   return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
         {/* Navigation */}
         <AppBar
            position='fixed'
            sx={{
               background:
                  'linear-gradient(135deg, rgba(1, 148, 154, 0.95) 0%, rgba(0, 67, 105, 0.95) 100%)',
               backdropFilter: 'blur(15px)',
               boxShadow: '0 2px 15px rgba(1, 148, 154, 0.2)',
               zIndex: 1100,
               color: 'white',
               border: 'none',
               borderRadius: 0,
            }}
         >
            <Toolbar sx={{ justifyContent: 'space-between', py: 0.5 }}>
               <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                  onClick={() => navigate('/')}
               >
                  <img
                     src={logo}
                     alt='WeTrippen Logo'
                     style={{ width: 32, height: 32, borderRadius: 6 }}
                  />
                  <Typography
                     variant='h6'
                     sx={{ fontWeight: 600, color: 'white', fontSize: '1.1rem' }}
                  >
                     WeTrippen
                  </Typography>
               </Box>

               {!isMobile ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                     <Button
                        component={RouterLink}
                        to='/login'
                        sx={{
                           color: 'rgba(255,255,255,0.9)',
                           textTransform: 'none',
                           fontSize: '0.9rem',
                           fontWeight: 400,
                        }}
                     >
                        Sign In
                     </Button>
                     <Button
                        onClick={scrollToForm}
                        variant='contained'
                        sx={{
                           background: 'rgba(255,255,255,0.2)',
                           color: 'white',
                           textTransform: 'none',
                           fontWeight: 500,
                           borderRadius: 2,
                           px: 2.5,
                           py: 0.5,
                           fontSize: '0.9rem',
                           backdropFilter: 'blur(10px)',
                           border: '1px solid rgba(255,255,255,0.3)',
                           '&:hover': {
                              background: 'rgba(255,255,255,0.3)',
                              transform: 'translateY(-1px)',
                           },
                        }}
                     >
                        Apply Now
                     </Button>
                  </Box>
               ) : (
                  <IconButton
                     onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                     sx={{ color: 'white' }}
                  >
                     {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                  </IconButton>
               )}
            </Toolbar>

            {/* Mobile Menu */}
            {isMobile && mobileMenuOpen && (
               <Box sx={{ bgcolor: 'white', borderTop: '1px solid', borderColor: 'divider', p: 2 }}>
                  <Button
                     fullWidth
                     component={RouterLink}
                     to='/login'
                     sx={{ justifyContent: 'flex-start', mb: 1, color: 'text.primary' }}
                  >
                     Sign In
                  </Button>
                  <Button
                     fullWidth
                     onClick={scrollToForm}
                     variant='contained'
                     sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                     Apply Now
                  </Button>
               </Box>
            )}
         </AppBar>

         {/* Hero Section */}
         <Box
            sx={{
               pt: 20,
               pb: 8,
               background:
                  'linear-gradient(135deg, rgba(1, 148, 154, 0.1) 0%, rgba(0, 67, 105, 0.1) 50%, rgba(255, 107, 107, 0.1) 100%)',
            }}
         >
            <Container maxWidth='md'>
               <Box sx={{ textAlign: 'center' }}>
                  <Box
                     sx={{
                        width: 60,
                        height: 60,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                        animation: `${float} 3s ease-in-out infinite`,
                     }}
                  >
                     <GroupsIcon sx={{ fontSize: 30, color: 'primary.main' }} />
                  </Box>
                  <Typography
                     variant='h2'
                     sx={{
                        fontWeight: 600,
                        mb: 2,
                        background: theme.palette.gradient?.primary,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                     }}
                  >
                     Join Our Community Ambassador Program
                  </Typography>
                  <Typography variant='h4' sx={{ fontWeight: 500, mb: 2, color: 'text.primary' }}>
                     Help Us Build Stronger Communities
                  </Typography>
                  <Typography
                     variant='body1'
                     sx={{
                        color: 'text.secondary',
                        mb: 3,
                        maxWidth: 600,
                        mx: 'auto',
                        fontSize: '1.1rem',
                     }}
                  >
                     Join passionate community builders who believe in bringing people together. Be
                     part of WeTrippen's mission to transform how people connect.
                  </Typography>
                  <Typography
                     variant='body2'
                     sx={{
                        color: 'text.secondary',
                        mb: 4,
                        maxWidth: 600,
                        mx: 'auto',
                        lineHeight: 1.6,
                     }}
                  >
                     We're looking for natural connectors, event organizers, and community builders
                     who share our vision of meaningful human connection. If you're already bringing
                     people together and want better tools to do it, we want to hear from you.
                  </Typography>
                  <Button
                     variant='contained'
                     startIcon={<StarIcon />}
                     onClick={scrollToForm}
                     sx={{ px: 3, py: 1, fontSize: '1rem' }}
                  >
                     Apply to Become an Ambassador
                  </Button>
               </Box>
            </Container>
         </Box>

         {/* Program Overview */}
         <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
            <Container maxWidth='md'>
               <Typography variant='h3' sx={{ textAlign: 'center', fontWeight: 600, mb: 6 }}>
                  What is a WeTrippen Community Ambassador?
               </Typography>

               <Card
                  sx={{
                     p: { xs: 4, md: 6 },
                     mb: 6,
                     boxShadow: '0 8px 30px rgba(1, 148, 154, 0.15)',
                  }}
               >
                  <CardContent sx={{ p: 0 }}>
                     <Typography
                        variant='body1'
                        sx={{ fontSize: '1.1rem', color: 'text.secondary', mb: 4, lineHeight: 1.7 }}
                     >
                        Community Ambassadors are passionate individuals who believe in the power of
                        organized communities. You're already hosting events, building groups, or
                        bringing people together in your area. We want to support your efforts with
                        better tools, early access to features, and recognition for the important
                        work you're doing.
                     </Typography>

                     <Typography
                        variant='h5'
                        sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}
                     >
                        What We're Looking For:
                     </Typography>

                     <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {[
                                 {
                                    title: 'Active Community Builders',
                                    desc: 'You currently organize events, host gatherings, or lead groups',
                                 },
                                 {
                                    title: 'Natural Connectors',
                                    desc: 'People seek you out when they want to meet others or find community',
                                 },
                                 {
                                    title: 'Mission Alignment',
                                    desc: 'You believe that organized communities make life better for everyone',
                                 },
                              ].map((item, index) => (
                                 <Box
                                    key={index}
                                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}
                                 >
                                    <CheckCircleIcon
                                       sx={{ color: 'success.main', mt: 0.5, fontSize: 20 }}
                                    />
                                    <Box>
                                       <Typography
                                          variant='subtitle1'
                                          sx={{ fontWeight: 600, mb: 0.5 }}
                                       >
                                          {item.title}
                                       </Typography>
                                       <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                          {item.desc}
                                       </Typography>
                                    </Box>
                                 </Box>
                              ))}
                           </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {[
                                 {
                                    title: 'Local Influence',
                                    desc: 'You have connections in your city, neighborhood, or interest community',
                                 },
                                 {
                                    title: 'Quality Focus',
                                    desc: 'You care more about meaningful connections than follower counts',
                                 },
                              ].map((item, index) => (
                                 <Box
                                    key={index}
                                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}
                                 >
                                    <CheckCircleIcon
                                       sx={{ color: 'success.main', mt: 0.5, fontSize: 20 }}
                                    />
                                    <Box>
                                       <Typography
                                          variant='subtitle1'
                                          sx={{ fontWeight: 600, mb: 0.5 }}
                                       >
                                          {item.title}
                                       </Typography>
                                       <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                          {item.desc}
                                       </Typography>
                                    </Box>
                                 </Box>
                              ))}
                           </Box>
                        </Grid>
                     </Grid>
                  </CardContent>
               </Card>
            </Container>
         </Box>

         {/* Benefits Section */}
         <Box sx={{ py: 8, bgcolor: 'grey.50' }}>
            <Container maxWidth='md'>
               <Typography variant='h3' sx={{ textAlign: 'center', fontWeight: 600, mb: 6 }}>
                  Ambassador Program Benefits
               </Typography>

               <Grid container spacing={4}>
                  {[
                     {
                        icon: <ZapIcon />,
                        title: 'Early Access & Input',
                        color: 'info',
                        benefits: [
                           'First access to new WeTrippen features and tools',
                           'Direct line to our product team for feedback and suggestions',
                           'Beta testing opportunities for upcoming releases',
                        ],
                     },
                     {
                        icon: <AwardIcon />,
                        title: 'Community & Recognition',
                        color: 'secondary',
                        benefits: [
                           'Connect with other passionate community builders nationwide',
                           'Featured recognition as a founding WeTrippen Ambassador',
                           'Exclusive Ambassador events and networking opportunities',
                        ],
                     },
                     {
                        icon: <ShieldIcon />,
                        title: 'Tools & Support',
                        color: 'success',
                        benefits: [
                           'Advanced features to enhance your community building efforts',
                           'Marketing materials and templates for your events',
                           'Direct support from the WeTrippen team',
                        ],
                     },
                     {
                        icon: <TrendingUpIcon />,
                        title: 'Growth Opportunities',
                        color: 'warning',
                        benefits: [
                           'Partnership discussions as the program evolves',
                           'Potential collaboration on community building initiatives',
                           'Opportunities to shape the future of community connection tools',
                        ],
                     },
                  ].map((benefit, index) => (
                     <Grid item xs={12} md={6} key={index}>
                        <Card
                           sx={{
                              p: 4,
                              height: '100%',
                              boxShadow: '0 4px 20px rgba(1, 148, 154, 0.1)',
                           }}
                        >
                           <CardContent sx={{ p: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                 <Box
                                    sx={{
                                       width: 48,
                                       height: 48,
                                       bgcolor: `${benefit.color}.light`,
                                       color: `${benefit.color}.main`,
                                       borderRadius: '50%',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       mr: 2,
                                    }}
                                 >
                                    {benefit.icon}
                                 </Box>
                                 <Typography variant='h6' sx={{ fontWeight: 600 }}>
                                    {benefit.title}
                                 </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                 {benefit.benefits.map((item, i) => (
                                    <Box
                                       key={i}
                                       sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
                                    >
                                       <ArrowForwardIcon
                                          sx={{
                                             color: `${benefit.color}.main`,
                                             fontSize: 16,
                                             mt: 0.5,
                                          }}
                                       />
                                       <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                          {item}
                                       </Typography>
                                    </Box>
                                 ))}
                              </Box>
                           </CardContent>
                        </Card>
                     </Grid>
                  ))}
               </Grid>
            </Container>
         </Box>

         {/* Application Form Section */}
         <Box id='application-form' sx={{ py: 8, bgcolor: 'background.paper' }}>
            <Container maxWidth='sm'>
               <Box sx={{ textAlign: 'center', mb: 6 }}>
                  <Typography variant='h3' sx={{ fontWeight: 600, mb: 2 }}>
                     Tell Us About Your Community Building
                  </Typography>
                  <Typography
                     variant='body1'
                     sx={{ color: 'text.secondary', maxWidth: 500, mx: 'auto' }}
                  >
                     We want to understand your passion for bringing people together. This isn't
                     about follower counts or marketing reach - it's about your genuine commitment
                     to building community.
                  </Typography>
               </Box>

               <Card
                  sx={{ p: { xs: 4, md: 6 }, boxShadow: '0 8px 30px rgba(1, 148, 154, 0.15)' }}
               >
                  <CardContent sx={{ p: 0 }}>
                     <form onSubmit={onSubmit}>
                        {/* Personal Information */}
                        <Box sx={{ mb: 6 }}>
                           <Typography
                              variant='h5'
                              sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}
                           >
                              Personal Information
                           </Typography>
                           <Grid container spacing={3}>
                              <Grid item xs={12} sm={6}>
                                 <TextField
                                    label='First Name *'
                                    value={formData.firstName}
                                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                                    fullWidth
                                    error={!!errors.firstName}
                                    helperText={errors.firstName}
                                 />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                 <TextField
                                    label='Last Name *'
                                    value={formData.lastName}
                                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                                    fullWidth
                                    error={!!errors.lastName}
                                    helperText={errors.lastName}
                                 />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                 <TextField
                                    label='Email Address *'
                                    type='email'
                                    value={formData.email}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                    fullWidth
                                    error={!!errors.email}
                                    helperText={errors.email}
                                 />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                 <TextField
                                    label='Phone Number (Optional)'
                                    type='tel'
                                    value={formData.phone}
                                    onChange={(e) => handleInputChange('phone', e.target.value)}
                                    fullWidth
                                 />
                              </Grid>
                              <Grid item xs={12}>
                                 <TextField
                                    label='City, State *'
                                    placeholder='e.g., San Francisco, CA'
                                    value={formData.cityState}
                                    onChange={(e) => handleInputChange('cityState', e.target.value)}
                                    fullWidth
                                    error={!!errors.cityState}
                                    helperText={errors.cityState}
                                 />
                              </Grid>
                           </Grid>
                        </Box>

                        {/* Community Building Experience */}
                        <Box sx={{ mb: 6 }}>
                           <Typography
                              variant='h5'
                              sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}
                           >
                              Community Building Experience
                           </Typography>
                           <Box sx={{ mb: 4 }}>
                              <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 2 }}>
                                 What type of community or group do you currently build/organize? *
                                 (Select all that apply)
                              </Typography>
                              <FormControl error={!!errors.communityType} fullWidth>
                                 <FormGroup>
                                    <Grid container spacing={2}>
                                       {[
                                          { value: 'local-meetups', label: 'Local Meetups' },
                                          { value: 'travel-groups', label: 'Travel Groups' },
                                          { value: 'family-events', label: 'Family Events' },
                                          {
                                             value: 'professional-networks',
                                             label: 'Professional Networks',
                                          },
                                          {
                                             value: 'greek-organizations',
                                             label: 'Greek Organizations',
                                          },
                                          {
                                             value: 'neighborhood-communities',
                                             label: 'Neighborhood Communities',
                                          },
                                          { value: 'hobby-groups', label: 'Hobby Groups' },
                                          { value: 'other', label: 'Other' },
                                       ].map((option) => (
                                          <Grid item xs={12} sm={6} key={option.value}>
                                             <FormControlLabel
                                                control={
                                                   <Checkbox
                                                      checked={formData.communityType.includes(
                                                         option.value
                                                      )}
                                                      onChange={(e) => {
                                                         const currentValues =
                                                            formData.communityType;
                                                         if (e.target.checked) {
                                                            handleInputChange('communityType', [
                                                               ...currentValues,
                                                               option.value,
                                                            ]);
                                                         } else {
                                                            handleInputChange(
                                                               'communityType',
                                                               currentValues.filter(
                                                                  (value: string) =>
                                                                     value !== option.value
                                                               )
                                                            );
                                                         }
                                                      }}
                                                   />
                                                }
                                                label={option.label}
                                             />
                                          </Grid>
                                       ))}
                                    </Grid>
                                 </FormGroup>
                                 {errors.communityType && (
                                    <FormHelperText>{errors.communityType}</FormHelperText>
                                 )}
                              </FormControl>
                           </Box>

                           <FormControl fullWidth error={!!errors.experienceLength}>
                              <InputLabel>
                                 How long have you been organizing or building community? *
                              </InputLabel>
                              <Select
                                 value={formData.experienceLength}
                                 onChange={(e) =>
                                    handleInputChange('experienceLength', e.target.value)
                                 }
                                 label='How long have you been organizing or building community? *'
                              >
                                 <MenuItem value='less-than-1'>Less than 1 year</MenuItem>
                                 <MenuItem value='1-2-years'>1-2 years</MenuItem>
                                 <MenuItem value='3-5-years'>3-5 years</MenuItem>
                                 <MenuItem value='5-plus-years'>5+ years</MenuItem>
                              </Select>
                              {errors.experienceLength && (
                                 <FormHelperText>{errors.experienceLength}</FormHelperText>
                              )}
                           </FormControl>
                        </Box>

                        {/* Tell Us Your Story */}
                        <Box sx={{ mb: 6 }}>
                           <Typography
                              variant='h5'
                              sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}
                           >
                              Tell Us Your Story
                           </Typography>
                           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <TextField
                                 label="Tell us about a community or group you've helped build *"
                                 helperText='Describe what you organized, how many people you brought together, and what made it successful. (200 word limit)'
                                 value={formData.communityStory}
                                 onChange={(e) =>
                                    handleInputChange('communityStory', e.target.value)
                                 }
                                 multiline
                                 rows={4}
                                 fullWidth
                                 error={!!errors.communityStory}
                              />

                              <TextField
                                 label="Most meaningful connection you've facilitated *"
                                 helperText='Share a specific example of how you helped people connect in a way that mattered. (150 word limit)'
                                 value={formData.meaningfulConnection}
                                 onChange={(e) =>
                                    handleInputChange('meaningfulConnection', e.target.value)
                                 }
                                 multiline
                                 rows={4}
                                 fullWidth
                                 error={!!errors.meaningfulConnection}
                              />

                              <TextField
                                 label='Why does community building matter to you? *'
                                 helperText='What drives your passion for bringing people together? (150 word limit)'
                                 value={formData.passion}
                                 onChange={(e) => handleInputChange('passion', e.target.value)}
                                 multiline
                                 rows={4}
                                 fullWidth
                                 error={!!errors.passion}
                              />

                              <TextField
                                 label='How do you currently organize your events/groups? *'
                                 helperText='What tools do you use, and what frustrates you about the current process? (150 word limit)'
                                 value={formData.currentTools}
                                 onChange={(e) => handleInputChange('currentTools', e.target.value)}
                                 multiline
                                 rows={4}
                                 fullWidth
                                 error={!!errors.currentTools}
                              />

                              <TextField
                                 label='Perfect community building platform (Optional)'
                                 helperText='If you could design the ideal tool, what features would it have? (100 word limit)'
                                 value={formData.idealPlatform}
                                 onChange={(e) =>
                                    handleInputChange('idealPlatform', e.target.value)
                                 }
                                 multiline
                                 rows={3}
                                 fullWidth
                              />
                           </Box>
                        </Box>

                        {/* Additional Information */}
                        <Box sx={{ mb: 6 }}>
                           <Typography
                              variant='h5'
                              sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}
                           >
                              Additional Information
                           </Typography>
                           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <TextField
                                 label='Social media platforms you use (Optional)'
                                 placeholder='e.g., Facebook Groups, Meetup, Discord, etc.'
                                 value={formData.socialPlatforms}
                                 onChange={(e) =>
                                    handleInputChange('socialPlatforms', e.target.value)
                                 }
                                 fullWidth
                              />

                              <TextField
                                 label='Additional information (Optional)'
                                 helperText="Anything else you'd like us to know about your community building experience? (100 word limit)"
                                 value={formData.additionalInfo}
                                 onChange={(e) =>
                                    handleInputChange('additionalInfo', e.target.value)
                                 }
                                 multiline
                                 rows={3}
                                 fullWidth
                              />
                           </Box>
                        </Box>

                        <Box sx={{ pt: 4 }}>
                           <Button
                              type='submit'
                              variant='contained'
                              size='large'
                              startIcon={isSubmitting ? null : <RocketIcon />}
                              fullWidth={isMobile}
                              disabled={isSubmitting}
                              sx={{ px: 6, py: 2, fontSize: '1.1rem' }}
                           >
                              {isSubmitting
                                 ? 'Submitting Application...'
                                 : 'Apply to Become an Ambassador'}
                           </Button>
                        </Box>
                     </form>
                  </CardContent>
               </Card>
            </Container>
         </Box>

         {/* Expectations Section */}
         <Box sx={{ py: 8, bgcolor: 'grey.50' }}>
            <Container maxWidth='md'>
               <Typography variant='h3' sx={{ textAlign: 'center', fontWeight: 600, mb: 6 }}>
                  What We Expect from Ambassadors
               </Typography>

               <Grid container spacing={4}>
                  {[
                     {
                        icon: <HeartIcon />,
                        title: 'Authentic Advocacy',
                        color: 'info',
                        expectations: [
                           'Share WeTrippen with your community when it genuinely helps them',
                           'Provide honest feedback about features and user experience',
                           'Use WeTrippen for your own community building activities',
                        ],
                     },
                     {
                        icon: <MessageCircleIcon />,
                        title: 'Active Participation',
                        color: 'success',
                        expectations: [
                           'Participate in quarterly Ambassador check-ins and feedback sessions',
                           'Test new features and provide thoughtful input',
                           'Share success stories and use cases with the WeTrippen team',
                        ],
                     },
                     {
                        icon: <TargetIcon />,
                        title: 'Community Leadership',
                        color: 'secondary',
                        expectations: [
                           'Continue building and nurturing your communities',
                           'Help other Ambassadors learn from your experience',
                           'Represent WeTrippen values of authentic connection and community',
                        ],
                     },
                  ].map((expectation, index) => (
                     <Grid item xs={12} md={4} key={index}>
                        <Card
                           sx={{
                              p: 4,
                              textAlign: 'center',
                              height: '100%',
                              boxShadow: '0 4px 20px rgba(1, 148, 154, 0.1)',
                           }}
                        >
                           <CardContent sx={{ p: 0 }}>
                              <Box
                                 sx={{
                                    width: 64,
                                    height: 64,
                                    bgcolor: `${expectation.color}.light`,
                                    color: `${expectation.color}.main`,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    mb: 3,
                                 }}
                              >
                                 {expectation.icon}
                              </Box>
                              <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>
                                 {expectation.title}
                              </Typography>
                              <Box sx={{ textAlign: 'left' }}>
                                 {expectation.expectations.map((item, i) => (
                                    <Box
                                       key={i}
                                       sx={{
                                          display: 'flex',
                                          alignItems: 'flex-start',
                                          gap: 1,
                                          mb: 1.5,
                                       }}
                                    >
                                       <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                          • {item}
                                       </Typography>
                                    </Box>
                                 ))}
                              </Box>
                           </CardContent>
                        </Card>
                     </Grid>
                  ))}
               </Grid>
            </Container>
         </Box>

         {/* Next Steps Section */}
         <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
            <Container maxWidth='sm'>
               <Typography variant='h3' sx={{ textAlign: 'center', fontWeight: 600, mb: 6 }}>
                  What Happens Next?
               </Typography>

               <Grid container spacing={4}>
                  {[
                     {
                        step: '1',
                        title: 'Application Review (1-2 weeks)',
                        description:
                           'Our team will review your application, focusing on your community building experience and alignment with our mission.',
                     },
                     {
                        step: '2',
                        title: 'Introduction Call (Selected Applicants)',
                        description:
                           "We'll schedule a brief conversation to learn more about your community building goals and how WeTrippen can support them.",
                     },
                     {
                        step: '3',
                        title: 'Welcome to the Program',
                        description:
                           'Accepted Ambassadors receive welcome materials, access to the Ambassador portal, and introduction to the community.',
                     },
                     {
                        step: '4',
                        title: 'Ongoing Partnership',
                        description:
                           'Regular communication, early access to features, and collaboration opportunities as the program grows.',
                     },
                  ].map((step, index) => (
                     <Grid item xs={12} sm={6} key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                           <Box
                              sx={{
                                 width: 32,
                                 height: 32,
                                 bgcolor: 'primary.main',
                                 color: 'white',
                                 borderRadius: '50%',
                                 display: 'flex',
                                 alignItems: 'center',
                                 justifyContent: 'center',
                                 fontWeight: 700,
                                 fontSize: '0.875rem',
                                 flexShrink: 0,
                                 mt: 0.5,
                              }}
                           >
                              {step.step}
                           </Box>
                           <Box>
                              <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
                                 {step.title}
                              </Typography>
                              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                 {step.description}
                              </Typography>
                           </Box>
                        </Box>
                     </Grid>
                  ))}
               </Grid>
            </Container>
         </Box>

         {/* Closing Section */}
         <Box
            sx={{
               py: 8,
               background: theme.palette.gradient?.primary,
               color: 'white',
               textAlign: 'center',
            }}
         >
            <Container maxWidth='sm'>
               <Box
                  sx={{
                     width: 60,
                     height: 60,
                     bgcolor: alpha('#fff', 0.2),
                     borderRadius: '50%',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     mx: 'auto',
                     mb: 3,
                  }}
               >
                  <SparklesIcon sx={{ fontSize: 30, color: 'white' }} />
               </Box>
               <Typography variant='h3' sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                  Ready to Help Us Transform Community Building?
               </Typography>
               <Typography
                  variant='body1'
                  sx={{ mb: 3, color: alpha('#fff', 0.9), maxWidth: 500, mx: 'auto' }}
               >
                  We believe that strong communities are built by passionate people who care about
                  bringing others together. If that describes you, we'd love to have you as part of
                  the WeTrippen Ambassador family.
               </Typography>

               <Button
                  variant='contained'
                  onClick={scrollToForm}
                  sx={{
                     bgcolor: 'white',
                     color: 'primary.main',
                     px: 3,
                     py: 1,
                     fontSize: '1rem',
                     '&:hover': {
                        bgcolor: 'grey.100',
                     },
                  }}
               >
                  Apply Now
               </Button>

               <Typography
                  variant='body2'
                  sx={{ color: alpha('#fff', 0.8), mt: 2, fontSize: '0.875rem' }}
               >
                  We review applications on a rolling basis and will respond within two weeks. Due
                  to the selective nature of this program, we're only able to accept a limited
                  number of Ambassadors at this time.
               </Typography>
            </Container>
         </Box>
      </Box>
   );
};

export default Ambassador;
